import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsApi } from '../lib/api';
import { useNotificationsStore } from '../store/notifications';
import { SectionLoader } from '../components/ui/PremiumLoader';
import {
  Bell,
  BookOpen,
  Video,
  CreditCard,
  Star,
  Trophy,
  AlertCircle,
  Check,
  CheckCheck,
  Trash2,
  X,
} from 'lucide-react';

// Notification types matching the backend enum
type NotificationType =
  | 'COURSE_ENROLLED'
  | 'COURSE_UPDATED'
  | 'MEETING_SCHEDULED'
  | 'MEETING_STARTING'
  | 'PAYMENT_RECEIVED'
  | 'NEW_REVIEW'
  | 'COURSE_COMPLETED'
  | 'SYSTEM';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: {
    courseId?: string;
    courseSlug?: string;
    meetingId?: string;
    enrollmentId?: string;
    paymentId?: string;
    reviewId?: string;
  };
  isRead: boolean;
  createdAt: string;
}

// Icon mapping for notification types
const notificationIcons: Record<NotificationType, typeof Bell> = {
  COURSE_ENROLLED: BookOpen,
  COURSE_UPDATED: BookOpen,
  MEETING_SCHEDULED: Video,
  MEETING_STARTING: Video,
  PAYMENT_RECEIVED: CreditCard,
  NEW_REVIEW: Star,
  COURSE_COMPLETED: Trophy,
  SYSTEM: AlertCircle,
};

// Color mapping for notification types
const notificationColors: Record<NotificationType, { bg: string; icon: string }> = {
  COURSE_ENROLLED: { bg: 'rgba(99, 102, 241, 0.15)', icon: '#6366f1' },
  COURSE_UPDATED: { bg: 'rgba(16, 185, 129, 0.15)', icon: '#10b981' },
  MEETING_SCHEDULED: { bg: 'rgba(245, 158, 11, 0.15)', icon: '#f59e0b' },
  MEETING_STARTING: { bg: 'rgba(239, 68, 68, 0.15)', icon: '#ef4444' },
  PAYMENT_RECEIVED: { bg: 'rgba(34, 197, 94, 0.15)', icon: '#22c55e' },
  NEW_REVIEW: { bg: 'rgba(251, 191, 36, 0.15)', icon: '#fbbf24' },
  COURSE_COMPLETED: { bg: 'rgba(139, 92, 246, 0.15)', icon: '#8b5cf6' },
  SYSTEM: { bg: 'rgba(156, 163, 175, 0.15)', icon: '#9ca3af' },
};

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export default function NotificationsPage() {
  const { decrementUnreadCount, clearUnreadCount } = useNotificationsStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll({
        unreadOnly: filter === 'unread',
        limit: 50,
      });
      if (response.data.success) {
        setNotifications(response.data.data as Notification[]);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      // Update the global unread count
      decrementUnreadCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      // Clear the global unread count
      clearUnreadCount();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(id);
    try {
      // Check if the notification being deleted is unread
      const notification = notifications.find((n) => n.id === id);
      const wasUnread = notification && !notification.isRead;
      
      await notificationsApi.delete(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      
      // Decrement unread count if the deleted notification was unread
      if (wasUnread) {
        decrementUnreadCount();
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationsApi.clear(true); // Clear only read notifications
      setNotifications((prev) => prev.filter((n) => !n.isRead));
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read on click - no navigation, just mark as read
    if (!notification.isRead) {
      // Update local state immediately for better UX
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
      );
      decrementUnreadCount();
      
      // Then sync with backend
      try {
        await notificationsApi.markAsRead(notification.id);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  if (isLoading) {
    return <SectionLoader className="h-full" />;
  }

  return (
    <div className="max-w-4xl mx-auto py-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
      >
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">
            Notifications
          </h1>
          <p className="text-white/40 text-sm mt-1">
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
              : 'All caught up!'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Tabs */}
          <div
            className="flex rounded-xl p-1"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {(['all', 'unread'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  filter === f
                    ? 'bg-white/10 text-white'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                {f === 'all' ? 'All' : 'Unread'}
              </button>
            ))}
          </div>

          {/* Actions */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white/60 
                       hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200"
            >
              <CheckCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}

          {notifications.some((n) => n.isRead) && (
            <button
              onClick={handleClearAll}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white/60 
                       hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-200"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Clear read</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* Notifications List */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        {notifications.length > 0 ? (
          <div className="divide-y divide-white/[0.04]">
            <AnimatePresence mode="popLayout">
              {notifications.map((notification, index) => {
                const Icon = notificationIcons[notification.type];
                const colors = notificationColors[notification.type];

                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20, height: 0 }}
                    transition={{ delay: index * 0.03, duration: 0.3 }}
                    className={`group relative flex items-start gap-4 p-5 cursor-pointer
                              transition-colors duration-200 ${
                                !notification.isRead
                                  ? 'bg-white/[0.02] hover:bg-white/[0.04]'
                                  : 'hover:bg-white/[0.02]'
                              }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Unread Indicator */}
                    {!notification.isRead && (
                      <div
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
                        style={{
                          background:
                            'linear-gradient(135deg, #dc464a 0%, #f97171 100%)',
                        }}
                      />
                    )}

                    {/* Icon */}
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: colors.bg }}
                    >
                      <Icon className="w-5 h-5" style={{ color: colors.icon }} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3
                            className={`font-medium text-sm ${
                              !notification.isRead ? 'text-white' : 'text-white/70'
                            }`}
                          >
                            {notification.title}
                          </h3>
                          <p className="text-white/40 text-sm mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        <span className="text-xs text-white/30 whitespace-nowrap flex-shrink-0">
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div
                      className="flex items-center gap-1 opacity-0 group-hover:opacity-100 
                                  transition-opacity duration-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!notification.isRead && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4 text-white/50 hover:text-green-400" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        disabled={isDeleting === notification.id}
                        className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                        title="Delete"
                      >
                        {isDeleting === notification.id ? (
                          <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                        ) : (
                          <X className="w-4 h-4 text-white/50 hover:text-red-400" />
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <Bell className="w-10 h-10 text-white/15" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </h3>
            <p className="text-white/40 text-sm text-center max-w-sm">
              {filter === 'unread'
                ? "You're all caught up! Check back later for new updates."
                : "When you get notifications, they'll appear here."}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

